import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  useAdminSessions,
  useAuditLog,
  useRevokeUserSession,
  useUser,
  useUserSessions,
} from '../../api/admin';
import { useAuth } from '../../auth/useAuth';
import { Page, PageHeader } from '../../components/Layout';
import { Avatar } from '../../components/Avatar';
import { ActiveBadge, Badge, PresenceDot, RoleBadge, SessionStatusBadge } from '../../components/Badge';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { Spinner } from '../../components/Spinner';
import {
  EditUserModal,
  ImpersonateModal,
  ResetPasswordModal,
  SuspendDialog,
  VerifyDialog,
} from './UserActions';
import {
  formatDateTime,
  formatMoney,
  formatNumber,
  formatRelative,
  formatSlotRange,
  humanizeAction,
} from '../../lib/format';
import { ApiError } from '../../lib/api';
import { TutorApplicationPanel } from '../tutors/TutorApplicationPanel';
import type { RegistrationStep } from '../../types/api';

/**
 * What an unfinished sign-up stopped short of, for the badge on the student
 * panel — an admin chasing the student needs to know which half to ask for,
 * not only that something is missing.
 *
 * A profile from an API that predates `registrationStep` still reads
 * "Incomplete", which is exactly what it said before.
 */
function unfinishedAt(step: RegistrationStep | undefined): string {
  if (step === 'education') return 'No grade yet';
  if (step === 'guardian') return 'No parent yet';
  return 'Incomplete';
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[0.6875rem] text-ink-3">{label}</dt>
      <dd className="mt-0.5 text-[0.8125rem] text-ink">{children}</dd>
    </div>
  );
}

export function UserDetailPage() {
  const { id = '' } = useParams();
  const { user: me } = useAuth();
  const userQuery = useUser(id);

  const [editOpen, setEditOpen] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [impersonateOpen, setImpersonateOpen] = useState(false);

  const isTutor = userQuery.data?.role === 'tutor';
  const sessions = useAdminSessions({
    scope: 'all',
    limit: 10,
    ...(isTutor ? { tutorId: id } : { studentId: id }),
  });
  const audit = useAuditLog({ targetId: id, limit: 10 });

  if (userQuery.isLoading) {
    return (
      <Page>
        <div className="flex h-64 items-center justify-center text-ink-3">
          <Spinner className="size-5" />
        </div>
      </Page>
    );
  }

  if (userQuery.isError || !userQuery.data) {
    return (
      <Page>
        <ErrorState
          title="Could not load this person"
          message={
            userQuery.error instanceof ApiError ? userQuery.error.message : undefined
          }
          onRetry={() => void userQuery.refetch()}
        />
      </Page>
    );
  }

  const user = userQuery.data;
  const isSelf = me?.id === user.id;
  const canImpersonate = user.role !== 'admin' && user.isActive && !isSelf;

  return (
    <Page>
      <nav className="mb-3 text-xs text-ink-3">
        <Link to="/users" className="hover:text-accent hover:underline">
          People
        </Link>
        <span className="mx-1.5">/</span>
        <span className="text-ink-2">{user.fullName}</span>
      </nav>

      <PageHeader
        title={user.fullName}
        actions={
          <>
            {user.role === 'student' && <Link className="btn btn-primary" to={`/learning-plans?studentId=${user.id}`}>Plans & lessons</Link>}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setEditOpen(true)}
            >
              Edit details
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setResetOpen(true)}
            >
              Set password
            </button>
            {isTutor && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setVerifyOpen(true)}
              >
                {user.tutorProfile?.isVerified ? 'Remove verification' : 'Verify tutor'}
              </button>
            )}
            {canImpersonate && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setImpersonateOpen(true)}
              >
                Act as this person
              </button>
            )}
            <button
              type="button"
              className={user.isActive ? 'btn btn-danger' : 'btn btn-primary'}
              onClick={() => setSuspendOpen(true)}
              disabled={isSelf && user.isActive}
              title={
                isSelf && user.isActive
                  ? 'You cannot suspend your own account'
                  : undefined
              }
            >
              {user.isActive ? 'Suspend' : 'Restore'}
            </button>
          </>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[20rem_1fr]">
        <div className="space-y-4">
          <div className="panel p-4">
            <div className="flex items-start gap-3">
              <Avatar name={user.fullName} src={user.avatarUrl} size="lg" />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-ink">{user.fullName}</div>
                <div className="truncate text-xs text-ink-3">{user.email}</div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <RoleBadge role={user.role} />
                  <ActiveBadge isActive={user.isActive} />
                  {user.tutorProfile?.isVerified && <Badge tone="good">Verified</Badge>}
                </div>
                <div className="mt-2">
                  <PresenceDot online={user.isOnline} />
                </div>
              </div>
            </div>

            <dl className="mt-4 grid grid-cols-2 gap-3 border-t border-rule pt-4">
              <Field label="Phone">{user.phone || '—'}</Field>
              <Field label="Timezone">{user.timezone}</Field>
              <Field label="Joined">
                <span title={new Date(user.createdAt).toLocaleString()}>
                  {formatRelative(user.createdAt)}
                </span>
              </Field>
              <Field label="Last seen">
                {user.lastLoginAt ? (
                  <span title={new Date(user.lastLoginAt).toLocaleString()}>
                    {formatRelative(user.lastLoginAt)}
                  </span>
                ) : (
                  'Never signed in'
                )}
              </Field>
              <Field label="Lessons">{formatNumber(user.sessionCount)}</Field>
              <Field label="Upcoming">{formatNumber(user.upcomingSessionCount)}</Field>
            </dl>

            <div className="mt-4 border-t border-rule pt-3">
              <dt className="text-[0.6875rem] text-ink-3">User ID</dt>
              <dd className="mono mt-0.5 break-all text-[0.6875rem] text-ink-2">{user.id}</dd>
            </div>
          </div>

          {user.tutorProfile && (
            <div className="panel p-4">
              <h2 className="mb-3 text-sm font-semibold text-ink">Tutor profile</h2>
              <dl className="space-y-3">
                <Field label="Headline">{user.tutorProfile.headline || '—'}</Field>
                <Field label="Rate">
                  {formatMoney(user.tutorProfile.hourlyRateCents, user.tutorProfile.currency)}
                  <span className="text-ink-3"> / hour</span>
                </Field>
                <Field label="Experience">
                  {user.tutorProfile.yearsExperience != null
                    ? `${user.tutorProfile.yearsExperience} years`
                    : '—'}
                </Field>
                <Field label="Rating">
                  {user.tutorProfile.ratingCount > 0
                    ? `${user.tutorProfile.ratingAvg.toFixed(2)} from ${formatNumber(
                        user.tutorProfile.ratingCount,
                      )} ratings`
                    : 'Not rated yet'}
                </Field>
                <Field label="Subjects">
                  {user.tutorProfile.subjects.length > 0 ? (
                    <span className="flex flex-wrap gap-1">
                      {user.tutorProfile.subjects.map((s) => (
                        <Badge key={s}>{s}</Badge>
                      ))}
                    </span>
                  ) : (
                    '—'
                  )}
                </Field>
                {user.tutorProfile.bio && (
                  <Field label="Bio">
                    <p className="whitespace-pre-wrap text-xs leading-relaxed text-ink-2">
                      {user.tutorProfile.bio}
                    </p>
                  </Field>
                )}
              </dl>
            </div>
          )}

          {user.tutorProfile && (
            <TutorApplicationPanel tutorId={user.id} profile={user.tutorProfile} />
          )}

          {user.studentProfile && (
            <div className="panel p-4">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-ink">Student profile</h2>
                {!user.studentProfile.profileComplete && (
                  <span className="rounded-full border border-rule-strong px-2 py-0.5 text-[0.6875rem] text-ink-2">
                    {unfinishedAt(user.studentProfile.registrationStep)}
                  </span>
                )}
              </div>
              <dl className="space-y-3">
                <Field label="Country">{user.studentProfile.countryCode || '—'}</Field>
                <Field label="Stage">{user.studentProfile.levelName || '—'}</Field>
                <Field label="Grade">
                  {/* gradeName is the catalog row; gradeLevel is the free text
                      it replaces, still shown for students who predate it. */}
                  {user.studentProfile.gradeName ?? user.studentProfile.gradeLevel ?? '—'}
                  {user.studentProfile.gradeNameAr && (
                    <span className="ml-2 text-ink-3" dir="rtl">
                      {user.studentProfile.gradeNameAr}
                    </span>
                  )}
                </Field>
                <Field label="Stream">
                  {user.studentProfile.streamName || '—'}
                  {user.studentProfile.streamNameAr && (
                    <span className="ml-2 text-ink-3" dir="rtl">
                      {user.studentProfile.streamNameAr}
                    </span>
                  )}
                </Field>
                <Field label="School">{user.studentProfile.schoolName || '—'}</Field>
                <Field label="Parent">
                  {user.studentProfile.guardianName || '—'}
                  {user.studentProfile.guardianRelationship && (
                    <span className="ml-2 text-ink-3">
                      ({user.studentProfile.guardianRelationship})
                    </span>
                  )}
                </Field>
                <Field label="Parent phone">
                  {user.studentProfile.guardianPhone ? (
                    // The admin team places trials by calling the parent, and
                    // WhatsApp is how that happens here.
                    <a
                      className="text-accent hover:underline"
                      href={`https://wa.me/${user.studentProfile.guardianPhone.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {user.studentProfile.guardianPhone}
                    </a>
                  ) : (
                    '—'
                  )}
                </Field>
                <Field label="Guardian email">
                  {user.studentProfile.guardianEmail || '—'}
                </Field>
              </dl>
            </div>
          )}

          <UserSessionsPanel userId={user.id} sharingSuspected={user.sharingSuspected} />
        </div>

        <div className="space-y-4">
          <div className="panel overflow-hidden">
            <div className="flex items-center justify-between border-b border-rule px-4 py-3">
              <h2 className="text-sm font-semibold text-ink">Recent lessons</h2>
              <Link
                to={`/sessions?${isTutor ? 'tutorId' : 'studentId'}=${user.id}`}
                className="text-xs text-accent hover:underline"
              >
                See all
              </Link>
            </div>

            {sessions.isLoading ? (
              <div className="flex h-32 items-center justify-center text-ink-3">
                <Spinner />
              </div>
            ) : sessions.data && sessions.data.data.length > 0 ? (
              <table className="data-table">
                <tbody>
                  {sessions.data.data.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <Link
                          to={`/sessions/${s.id}`}
                          className="font-medium text-ink hover:text-accent hover:underline"
                        >
                          {isTutor ? s.student.fullName : s.tutor.fullName}
                        </Link>
                        <div className="text-xs text-ink-3">
                          {s.subject ?? 'No subject given'}
                        </div>
                      </td>
                      <td className="text-xs text-ink-2">
                        {formatSlotRange(s.startTime, s.endTime)}
                      </td>
                      <td>
                        <SessionStatusBadge status={s.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <EmptyState
                title="No lessons yet"
                description={
                  user.role === 'student'
                    ? 'This student has not booked anything.'
                    : 'Nobody has booked this tutor yet.'
                }
              />
            )}
          </div>

          <div className="panel overflow-hidden">
            <div className="flex items-center justify-between border-b border-rule px-4 py-3">
              <h2 className="text-sm font-semibold text-ink">
                Admin actions on this account
              </h2>
              <Link
                to={`/audit?targetId=${user.id}`}
                className="text-xs text-accent hover:underline"
              >
                See all
              </Link>
            </div>

            {audit.isLoading ? (
              <div className="flex h-32 items-center justify-center text-ink-3">
                <Spinner />
              </div>
            ) : audit.data && audit.data.data.length > 0 ? (
              <ul className="divide-y divide-rule">
                {audit.data.data.map((entry) => (
                  <li key={entry.id} className="px-4 py-2.5">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span className="text-[0.8125rem] font-medium text-ink">
                        {humanizeAction(entry.action)}
                      </span>
                      <span className="text-xs text-ink-3">by {entry.adminName}</span>
                      <span
                        className="ml-auto text-xs text-ink-3"
                        title={formatDateTime(entry.createdAt)}
                      >
                        {formatRelative(entry.createdAt)}
                      </span>
                    </div>
                    {typeof entry.metadata?.reason === 'string' && (
                      <p className="mt-0.5 text-xs text-ink-2">
                        “{entry.metadata.reason}”
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState
                title="Nothing recorded yet"
                description="Admin actions taken on this account will appear here."
              />
            )}
          </div>
        </div>
      </div>

      <EditUserModal user={user} open={editOpen} onClose={() => setEditOpen(false)} />
      <ResetPasswordModal user={user} open={resetOpen} onClose={() => setResetOpen(false)} />
      <SuspendDialog user={user} open={suspendOpen} onClose={() => setSuspendOpen(false)} />
      {isTutor && (
        <VerifyDialog user={user} open={verifyOpen} onClose={() => setVerifyOpen(false)} />
      )}
      {canImpersonate && (
        <ImpersonateModal
          user={user}
          open={impersonateOpen}
          onClose={() => setImpersonateOpen(false)}
        />
      )}
    </Page>
  );
}

/**
 * Where this account has been signed in.
 *
 * Addresses are unmasked here, unlike the student's own view: an admin
 * looking into a shared account needs to see whether two sessions came from
 * different networks. `replaced` is the one that matters — it means another
 * device signed in and ended that session.
 */
function UserSessionsPanel({
  userId,
  sharingSuspected,
}: {
  userId: string;
  sharingSuspected: boolean;
}) {
  const sessionsQuery = useUserSessions(userId);
  const revoke = useRevokeUserSession(userId);
  const sessions = sessionsQuery.data ?? [];

  return (
    <div className="panel p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-ink">Devices</h2>
        {sharingSuspected && (
          <span className="rounded-full border border-rule-strong px-2 py-0.5 text-[0.6875rem] text-ink-2">
            Sharing suspected
          </span>
        )}
      </div>

      {sessionsQuery.isLoading && <p className="text-[0.8125rem] text-ink-3">Loading…</p>}
      {sessionsQuery.isError && (
        <p className="text-[0.8125rem] text-danger">Could not load this user's devices.</p>
      )}
      {!sessionsQuery.isLoading && sessions.length === 0 && (
        <p className="text-[0.8125rem] text-ink-3">
          This account has not signed in since device history was kept.
        </p>
      )}

      <ul className="space-y-2">
        {sessions.map((s) => (
          <li key={s.id} className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-[0.8125rem] text-ink">
                {s.deviceLabel}
                {!s.revokedAt && <span className="ml-2 text-[0.6875rem] text-ink-3">live</span>}
              </p>
              <p className="text-[0.6875rem] text-ink-3">
                {s.ipAddress} · {formatRelative(s.lastSeenAt)}
                {s.revokedAt && ` · ended (${s.revokedReason})`}
              </p>
            </div>
            {!s.revokedAt && (
              <button
                type="button"
                className="btn btn-danger btn-sm shrink-0"
                disabled={revoke.isPending}
                onClick={() => revoke.mutate(s.id)}
              >
                Sign out
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
